import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { Field, fieldHintId } from "../components/Layout";
import { Icon, type IconName } from "../components/Icon";
import { LogoMark, Wordmark } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { useMe } from "../app/auth";

const POINTS: { icon: IconName; text: string }[] = [
  { icon: "phone", text: "Captured on the road" },
  { icon: "review", text: "Checked by engineers" },
  { icon: "roads", text: "Reported by chainage" },
];

/** Only a path inside this app. Browsers read "/\host" (and "//host") as another site. */
function safeNext(v: string | null) {
  return v && v.startsWith("/") && !v.startsWith("//") && !v.includes("\\") && !/[\u0000-\u001f]/.test(v) ? v : "/";
}

export function SignInPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const setup = useQuery({ queryKey: ["setup"], queryFn: () => api<{ needs_first_admin: boolean }>("/api/auth/setup") });

  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  if (me.isSuccess) return <Navigate to={next} replace />;
  const firstRun = setup.data?.needs_first_admin;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (firstRun) await api("/api/auth/first-admin", { method: "POST", json: { username, full_name: fullName, password } });
      else await api("/api/auth/login", { method: "POST", json: { username, password, remember } });
      await qc.invalidateQueries({ queryKey: ["me"] });
      nav(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const hint = [firstRun ? "At least 8 characters." : "", capsLock ? "Caps Lock is on." : ""].filter(Boolean).join(" ") || undefined;
  const watchCaps = (e: React.KeyboardEvent) => setCapsLock(e.getModifierState("CapsLock"));

  return (
    <div className="auth">
      <section className="auth-brand">
        <div className="auth-logo">
          <LogoMark size={40} />
          <Wordmark className="auth-wordmark" />
        </div>
        <div className="auth-pitch">
          <h1>Road condition, inventory and safety for Kenya's network.</h1>
          <ul className="auth-points">
            {POINTS.map((p) => <li key={p.text}><Icon name={p.icon} size={20} />{p.text}</li>)}
          </ul>
        </div>
        <svg className="auth-road" viewBox="0 0 200 200" aria-hidden="true">
          <path d="M60 200 95 0h10l35 200" fill="none" stroke="var(--on-dark)" strokeWidth="6" />
          <path className="auth-road-line" d="M100 0v200" stroke="var(--signal)" strokeWidth="5" />
        </svg>
      </section>

      <section className="auth-form">
        <ThemeToggle className="auth-theme" />
        <div className="stack" style={{ gap: "var(--s-1)" }}>
          <h2>{firstRun ? "Create the first administrator" : "Sign in"}</h2>
          <p className="muted">{firstRun ? "No administrator exists yet. This account will manage users and road access." : "Use the account your administrator gave you."}</p>
        </div>
        <form className="stack" style={{ gap: "var(--s-4)" }} onSubmit={submit} noValidate={false}>
          <Field label="Username" htmlFor="username">
            <input id="username" className="input" autoComplete="username" required autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
          </Field>
          {firstRun ? (
            <Field label="Full name" htmlFor="full-name">
              <input id="full-name" className="input" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </Field>
          ) : null}
          <Field label="Password" htmlFor="password" hint={hint} liveHint>
            <div className="input-with-action">
              <input id="password" className="input" type={showPassword ? "text" : "password"} required minLength={firstRun ? 8 : undefined}
                     autoComplete={firstRun ? "new-password" : "current-password"} value={password} aria-describedby={hint ? fieldHintId("password") : undefined}
                     onChange={(e) => setPassword(e.target.value)} onKeyDown={watchCaps} onKeyUp={watchCaps} />
              <button type="button" className="btn btn-ghost btn-icon" aria-label="Show password" aria-pressed={showPassword}
                      onClick={() => setShowPassword((v) => !v)}>
                <Icon name={showPassword ? "eyeOff" : "eye"} />
              </button>
            </div>
          </Field>
          {!firstRun ? (
            <label className="check"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />Keep me signed in on this device</label>
          ) : null}
          {error ? <p className="field-msg bad auth-error" role="alert"><Icon name="defects" />{error}</p> : null}
          <button className="btn btn-primary auth-submit" type="submit" disabled={busy || !username || !password} aria-busy={busy}>
            {busy ? <><span className="spinner" aria-hidden="true" />Signing in…</> : firstRun ? "Create administrator" : "Sign in"}
          </button>
          {!firstRun ? <p className="auth-note">Forgot your password? Ask your administrator to reset it. For anything else, email <a href="mailto:support@roadricsai.com">support@roadricsai.com</a>.</p> : null}
        </form>
        <footer className="auth-foot">
          <span>RoadricsAI {__APP_VERSION__}</span>
          <span className="auth-partner"><img src="/onsite-logo.png" alt="" width={32} height={31} />Onsite Consultants</span>
        </footer>
      </section>
    </div>
  );
}
